import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { saveAs } from 'file-saver';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { BackendService } from '../../core/backend.service';
import { BlobBackendService } from '../../core/blob-backend.service';
import { CONFIG } from '../../core/config';
import { ApiResult } from '../../core/messages/api-result';
import { DisplayMessage } from '../../core/messages/display-message';
import { Severity } from '../../shared/enums/severity';
import { LookupPackage, ModelLookupPackage, ModelPackage } from '../../viewmodels/data-uploads/download-package';
import { RcmDownloadRequest } from '../../viewmodels/models/rcm-publish-request';

@Injectable()
export class ModelPackageService {
  public modelPublished: BehaviorSubject<ModelPackage> = new BehaviorSubject<ModelPackage>(null);
  constructor(private backendService: BackendService, private blobBackendService: BlobBackendService, private messageService: MessageService, private i18NextPipe: I18NextPipe) { }

  public onModelPublished(model: ModelPackage) {
    this.modelPublished.next(model);
  }

  public downloadModel(id: string) {
    this.backendService
      .getById<ModelPackage>(ModelPackage, id)
      .pipe(take(1))
      .subscribe(
        data => {
          if (data) {
            const blob = new Blob([data.metadata], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');

            link.download = data.name + data.fileExtension;
            link.href = url;
            document.body.appendChild(link);

            link.click();
            document.body.removeChild(link);
          }
        },
        (errorResult: ApiResult) => {
          if (errorResult) {
            this.messageService.clear();
            errorResult.messages.forEach(message =>
              this.messageService.add(new DisplayMessage('Download Failed', message.text, errorResult.severity)),
            );
          }
        },
      );
  }

  public downloadModelLookups(id: string) {
    this.backendService
      .getById<ModelLookupPackage>(ModelLookupPackage, id)
      .pipe(take(1))
      .subscribe(
        data => {
          this.createLookupDownload(data);
        },
        (errorResult: ApiResult) => {
          this.handleDownloadError(errorResult);
        },
      );
  }

  public downloadFilteredModelLookups(modelId: string, lookupIds: string[]) {
    const params = new HttpParams().set('lookupIds', JSON.stringify(lookupIds));

    this.backendService
      .getByIdArrayRoute<ModelLookupPackage>(ModelLookupPackage, [modelId], new ModelLookupPackage().apiRoute, true, params)
      .pipe(take(1))
      .subscribe(
        data => this.createLookupDownload(data),
        (errorResult: ApiResult) => this.handleDownloadError(errorResult),
      );
  }

  public downloadLookup(id: string) {
    this.backendService
      .getById<LookupPackage>(LookupPackage, id)
      .pipe(take(1))
      .subscribe(
        data => {
          this.createLookupDownload(data);
        },
        (errorResult: ApiResult) => {
          this.handleDownloadError(errorResult);
        },
      );
  }

  private handleDownloadError(errorResult: ApiResult) {
    if (errorResult) {
      this.messageService.clear();
      errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Download Failed', message.text, errorResult.severity)));
    }
  }

  private createLookupDownload(data: ModelLookupPackage) {
    if (data) {
      const blob = new Blob([data.metadata], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.download = `${data.name}-lookups-${moment().format()}${data.fileExtension}`;
      link.href = url;
      document.body.appendChild(link);

      link.click();
      document.body.removeChild(link);
    }
  }

  uploadModelPackageFile(file: File) {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const modelPackage = new ModelPackage();

      modelPackage.name = file.name;
      modelPackage.metadata = reader.result;
      this.uploadModelPackage(modelPackage);
    };

    reader.readAsText(file);
  }

  uploadLookupPackageFile(file: File) {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const modelPackage = new ModelLookupPackage();

      modelPackage.name = file.name;
      modelPackage.metadata = reader.result;
      this.uploadLookupPackage(modelPackage);
    };

    reader.readAsText(file);
  }

  public uploadModelPackage(modelPackage: ModelPackage) {
    const header: string = this.i18NextPipe.transform('AdminModelsUploadMessage.ModelUpload');

    this.backendService
      .create<ModelPackage>(ModelPackage, modelPackage)
      .pipe(take(1))
      .subscribe(
        data => {
          if (data) {
            this.messageService.add(new DisplayMessage(header, this.i18NextPipe.transform('AdminModelsUploadMessage.ModelSuccessfullyUploaded'), Severity.Success));
            this.onModelPublished(data);
          }
        },
        (errorResult: ApiResult) => {
          if (errorResult) {
            this.messageService.clear();
            errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage(header, message.text, errorResult.severity)));
          }
        },
      );
  }

  public uploadLookupPackage(lookupPackage: ModelLookupPackage) {
    const header: string = this.i18NextPipe.transform('AdminModelsUploadMessage.LookupUpload');

    this.backendService
      .create<ModelLookupPackage>(ModelLookupPackage, lookupPackage)
      .pipe(take(1))
      .subscribe(
        data => {
          if (data) {
            this.messageService.add(new DisplayMessage(header, this.i18NextPipe.transform('AdminModelsUploadMessage.LookupsUploadCompleted'), Severity.Success));
          }
        },
        (errorResult: ApiResult) => {
          if (errorResult) {
            this.messageService.clear();
            errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage(header, message.text, errorResult.severity)));
          }
        },
      );
  }

  /**
   * Downloads an RCM Library to the browser and saves it
   * @param publishRequest object containing library details
   */
  downloadRcmPackage(publishRequest: RcmDownloadRequest) {
    this.requestRcmPackage(publishRequest, blob => {
      saveAs(blob, publishRequest.modelName + '_' + moment().format('YYMMDD') + CONFIG.fileExtensions.rcmPackage);
    });
  }

  /**
   * Downloads a document and, if successful, calls the callback function with the resulting blob
   * @param downloadRequest details of the package to download
   * @param callback function to be called on successful download
   */
  private requestRcmPackage(downloadRequest: RcmDownloadRequest, callback: (blob: Blob) => void) {
    this.blobBackendService.getByObject<RcmDownloadRequest>(RcmDownloadRequest, downloadRequest).subscribe(
      blob => {
        if (blob) {
          callback(blob);
        } else {
          this.messageService.clear();
          this.messageService.add(
            new DisplayMessage('The document could not be downloaded', 'The response was not in the expected format', Severity.Error),
          );
        }
      },
      (errorResult: ApiResult) => {
        this.messageService.clear();
        errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage('Download Failed', message.text, errorResult.severity)));
      },
    );
  }

  uploadRcmPackage(file: File) {
    const header: string = this.i18NextPipe.transform('AdminModelsUploadMessage.RCMPackageUpload');

    this.backendService
      .uploadDocument('0', file.name, file, CONFIG.apiRoutes.rcmLibraryPackageUpload)
      .pipe(take(1))
      .subscribe(
        data => {
          if (data) {
            this.messageService.clear();
            const apiResult: ApiResult = data as ApiResult;

            if (apiResult) {
              this.messageService.add(new DisplayMessage(header, this.i18NextPipe.transform('AdminModelsUploadMessage.RCMPackageUploadCompleted'), apiResult.severity));
              apiResult.messages.forEach(message => this.messageService.add(new DisplayMessage('', message.text, message.severity)));
            } else {
              this.messageService.add(new DisplayMessage(header, this.i18NextPipe.transform('AdminModelsUploadMessage.RCMPackageUploadCompleted'), Severity.Success));
            }
          }
        },
        (errorResult: ApiResult) => {
          if (errorResult) {
            this.messageService.clear();
            errorResult.messages.forEach(message => this.messageService.add(new DisplayMessage(header, message.text, errorResult.severity)));
          }
        },
      );
  }
}
